import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { BookOpen, Check, Feather, NotebookPen, PenLine, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";
import { activateWriterMode } from "@/lib/writer.functions";

export const Route = createFileRoute("/_authenticated/start-writing")({
  head: () => ({
    meta: [
      { title: "Start writing — activate Writer Mode on Quibblies" },
      {
        name: "description",
        content:
          "Turn on Writer Mode on your existing Quibblies account to publish fiction, personal experiences, reflections, journals, poetry and series.",
      },
      { property: "og:title", content: "Start writing on Quibblies" },
      {
        property: "og:description",
        content: "Activate Writer Mode on your existing Quibblies account — no second account needed.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: StartWritingPage,
});

const FORMS = [
  { icon: BookOpen, label: "Fiction", copy: "Imagined worlds, characters and stories." },
  { icon: Feather, label: "Personal experiences", copy: "Real-life writing, told your way." },
  { icon: Sparkles, label: "Personal reflections", copy: "Thoughts, feelings, desire and identity." },
  { icon: NotebookPen, label: "Journals & diaries", copy: "Ongoing personal journal writing." },
  { icon: PenLine, label: "Poetry & short-form", copy: "Verse and shorter pieces." },
  { icon: BookOpen, label: "Series & multi-chapter", copy: "Long-form work, chapter by chapter." },
] as const;

function StartWritingPage() {
  const { isWriter, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [accepted, setAccepted] = React.useState(false);
  const [penName, setPenName] = React.useState("");

  const activate = useServerFn(activateWriterMode);
  const mutation = useMutation({
    mutationFn: () =>
      activate({ data: { acceptedRules: accepted, ...(penName ? { penName } : {}) } }),

    onSuccess: async () => {
      await refreshProfile();
      toast.success("Writer Mode is active — welcome to Creator Studio.");
      void navigate({ to: "/studio" });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isWriter) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6">
        <h1 className="font-display text-3xl text-foreground">Writer Mode is already active</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Your Creator Studio is ready whenever you are.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button asChild variant="hero" size="pill">
            <Link to="/studio">Open Creator Studio</Link>
          </Button>
          <Button asChild variant="soft" size="pill">
            <Link to="/write">Create publication</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
        Same account, new tools
      </p>
      <h1 className="mt-3 font-display text-4xl tracking-tight text-foreground sm:text-5xl">
        Start writing on Quibblies
      </h1>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground">
        Writer Mode adds publishing tools to the account you already have,{" "}
        {profile?.display_name ? `${profile.display_name}. ` : ". "}
        Your profile, followers, bookmarks, comments, reading history and notifications all stay
        exactly where they are. A Writer is still a Reader.
      </p>

      <section className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {FORMS.map((form) => (
          <div key={form.label} className="rounded-2xl border border-border/60 bg-surface/60 p-4">
            <form.icon className="h-5 w-5 text-primary" aria-hidden="true" />
            <h2 className="mt-3 text-sm font-semibold text-foreground">{form.label}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{form.copy}</p>
          </div>
        ))}
      </section>

      <section className="mt-10 rounded-3xl border border-border/60 bg-surface/60 p-6">
        <h2 className="font-display text-2xl text-foreground">How publishing works</h2>
        <ol className="mt-4 space-y-3 text-sm text-muted-foreground">
          {[
            "Draft privately in Creator Studio — nothing is public while you work.",
            "Submit for review when you're ready.",
            "A moderator reviews against the Community Guidelines and privacy rules.",
            "Approved work is published. Otherwise you get changes requested, or a rejection with reasons you can appeal.",
          ].map((step, index) => (
            <li key={step} className="flex gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                {index + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
        <p className="mt-4 flex items-start gap-2 rounded-2xl border border-border/60 bg-background/60 p-4 text-sm text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          Nothing becomes public just because you press publish. Every publication is reviewed
          first.
        </p>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <Link to="/help" className="text-primary underline-offset-4 hover:underline">
            Publishing guidelines
          </Link>
          <Link to="/guidelines" className="text-primary underline-offset-4 hover:underline">
            Community guidelines
          </Link>
          <Link to="/privacy" className="text-primary underline-offset-4 hover:underline">
            Privacy guidance
          </Link>
        </div>
      </section>

      <section className="mt-10 rounded-3xl border border-primary/30 bg-primary/5 p-6">
        <h2 className="font-display text-2xl text-foreground">Activate Writer Mode</h2>

        <div className="mt-5 max-w-sm space-y-2">
          <Label htmlFor="pen-name">Pen name (optional)</Label>
          <Input
            id="pen-name"
            value={penName}
            onChange={(event) => setPenName(event.target.value)}
            placeholder="The name readers will see"
            maxLength={60}
          />
          <p className="text-xs text-muted-foreground">
            A pen name keeps your publishing identity separate from your account. Your email, date
            of birth and other private account details are never shown publicly. You can add or
            change pen names later.
          </p>
        </div>

        <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
          <Checkbox
            checked={accepted}
            onCheckedChange={(value) => setAccepted(value === true)}
            aria-label="Accept the publishing rules"
          />
          <span>
            I have read and accept the Quibblies publishing rules, community guidelines and privacy
            guidance. I understand every publication is reviewed before it appears publicly, and
            that I am responsible for protecting my privacy and the privacy of people mentioned in
            my writing.
          </span>
        </label>

        <Button
          variant="hero"
          size="pill"
          className="mt-6"
          disabled={!accepted || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
          {mutation.isPending ? "Activating…" : "Activate Writer Mode"}
        </Button>
      </section>
    </div>
  );
}
